import { LmvVector3 } from "./LmvVector3";

const _v1 = new LmvVector3();
const _v2 = new LmvVector3();
const _v3 = new LmvVector3();

export function packNormal(dst, dstOff, nx, ny, nz) {
  dst[dstOff] = (Math.atan2(ny, nx) / Math.PI + 1.0) * 0.5 * 65535 | 0;
  dst[dstOff + 1] = (nz + 1.0) * 0.5 * 65535 | 0;
}

export function generateSmoothVertexNormals(geom, packNormals) {

  if (geom.vblayout.normal || geom.isLines || geom.isPoints) {
    return;
  }

  if (!geom.vblayout.position) {
    console.warn("missing position channel");
    return;
  }

  const vb = geom.vb;
  const vbstride = geom.vbstride;
  const nstride = packNormals ? 1 : 3;
  const vbstride2 = geom.vbstride + nstride;
  const vcount = vb.length / geom.vbstride;
  const norms = new Float32Array(vcount * 3);
  const poffset = geom.vblayout.position.offset;

  if (poffset !== 0) {
    console.warn("unexpected position attribute offset");
  }

  if (!geom.indices) {
    geom.indices = new Uint16Array(vcount);
    for (let i = 0; i < vcount; i++) {
      geom.indices[i] = i;
    }
  }

  const ib = geom.indices || geom.ib;

  //Calculate interpolated normals for each vertex, based on
  //adjacent triangle normals.
  for (let i = 0; i < ib.length; i += 3) {

    let i1 = ib[i];
    let i2 = ib[i + 1];
    let i3 = ib[i + 2];

    let vi1 = i1 * vbstride + poffset;
    let vi2 = i2 * vbstride + poffset;
    let vi3 = i3 * vbstride + poffset;

    _v1.set(vb[vi1], vb[vi1 + 1], vb[vi1 + 2]);
    _v2.set(vb[vi2], vb[vi2 + 1], vb[vi2 + 2]);
    _v3.set(vb[vi3], vb[vi3 + 1], vb[vi3 + 2]);

    _v2.sub(_v1);
    _v3.sub(_v1);
    _v2.cross(_v3);

    _v2.normalize();

    norms[3 * i1] += _v2.x;
    norms[3 * i1 + 1] += _v2.y;
    norms[3 * i1 + 2] += _v2.z;

    norms[3 * i2] += _v2.x;
    norms[3 * i2 + 1] += _v2.y;
    norms[3 * i2 + 2] += _v2.z;

    norms[3 * i3] += _v2.x;
    norms[3 * i3 + 1] += _v2.y;
    norms[3 * i3 + 2] += _v2.z;
  }

  for (let i = 0; i < norms.length; i += 3) {
    _v1.set(norms[i], norms[i + 1], norms[i + 2]);
    _v1.normalize();

    norms[i] = _v1.x;
    norms[i + 1] = _v1.y;
    norms[i + 2] = _v1.z;
  }

  //Generate the new expanded stride interleaved vertex buffer.
  //The renderer expect normals to be at offset 3 (after the position)
  //So we have to insert the normals channel in before any other channels (uv, color, etc)
  //and then update the vertex layout accordingly

  const vb2 = new Float32Array(vcount * vbstride2);
  const uivb2 = new Uint16Array(vb2.buffer); //used for packed normals
  const dstRest = 3 + nstride;

  for (let i = 0; i < vcount; i++) {

    const srcOff = i * vbstride;
    const dstOff = i * vbstride2;

    //copy position
    for (let j = 0; j < 3; j++) {
      vb2[dstOff + j] = vb[srcOff + j];
    }

    //insert the normal
    if (packNormals) {
      let nx = norms[3 * i];
      let ny = norms[3 * i + 1];
      let nz = norms[3 * i + 2];

      packNormal(uivb2, 2 * (dstOff + 3), nx, ny, nz);
    } else {
      vb2[dstOff + 3] = norms[3 * i];
      vb2[dstOff + 4] = norms[3 * i + 1];
      vb2[dstOff + 5] = norms[3 * i + 2];
    }

    //copy remaining vertex data after the normal
    for (let j = 3; j < vbstride; j++) {
      vb2[dstOff + dstRest + j] = vb[srcOff + j];
    }
  }

  //Update the geometry data
  geom.vb = vb2;
  geom.vbstride = vbstride2;

  for (let item in geom.vblayout) {

    //position offset does not change as it comes before the normals
    if (item === "position") {
      continue;
    }

    geom.vblayout[item].offset += nstride;
  }

  //Add the new normal attribute to the vertex layout
  geom.vblayout.normal = {
    bytesPerItem: packNormals ? 2 : 4,
    offset: 3,
    normalized: true,
    itemSize: packNormals ? 2 : 3
  };
}

class VertexDedup {

  #vmap = new Map();

  #makeHash(buf, baseIndex, nAttrs, nx, ny, nz) {

    let res = "";
    for (let i = 0; i < nAttrs; i++) {
      res += buf[baseIndex + i].toPrecision(7);
      res += ":";
    }

    res += nx.toPrecision(7) + ":" +
    ny.toPrecision(7) + ":" +
    nz.toPrecision(7);

    return res;
  }

  add(buf, baseIndex, nAttrs, nx, ny, nz, oldIdx) {
    const h = this.#makeHash(buf, baseIndex, nAttrs, nx, ny, nz);

    const val = this.#vmap.get(h);

    if (val !== undefined) {
      return val[0];
    }

    const res = this.#vmap.size;
    this.#vmap.set(h, [res, nx, ny, nz, oldIdx]);

    return res;
  }

  size() {
    return this.#vmap.size;
  }

  forEach(cb) {
    for (const v of this.#vmap.values()) {
      cb(...v);
    }
  }
}

export function generateFlatVertexNormals(geom, packNormals) {

  if (geom.vblayout.normal || geom.isLines || geom.isPoints) {
    return;
  }

  const vb = geom.vb;
  const vbstride = geom.vbstride;
  const poffset = geom.vblayout.position.offset;
  const nAttrs = geom.vbstride;

  //we will have to duplicate vertices that have different normals,
  //but we can combine ones that have the same normal and position (and other attributes)
  let vertexRemap = {}; // old -> new vertex index
  const dedup = new VertexDedup();
  const addRemapEntry = (oldIdx, newIdx) => {
    if (vertexRemap[oldIdx] === undefined) {
      vertexRemap[oldIdx] = [newIdx];
    } else {
      vertexRemap[oldIdx].push(newIdx);
    }
  };

  const ib = geom.indices || geom.ib;
  const ibNew = [];

  //Loop over all triangles to generate new vertices with flat triangle normals
  for (let i = 0; i < ib.length; i += 3) {

    let i1 = ib[i];
    let i2 = ib[i + 1];
    let i3 = ib[i + 2];

    let vi1 = i1 * vbstride + poffset;
    let vi2 = i2 * vbstride + poffset;
    let vi3 = i3 * vbstride + poffset;

    _v1.set(vb[vi1], vb[vi1 + 1], vb[vi1 + 2]);
    _v2.set(vb[vi2], vb[vi2 + 1], vb[vi2 + 2]);
    _v3.set(vb[vi3], vb[vi3 + 1], vb[vi3 + 2]);

    //Compute triangle normal
    _v2.sub(_v1);
    _v3.sub(_v1);
    _v2.cross(_v3).normalize();

    const ni1 = dedup.add(vb, vi1, vbstride, _v2.x, _v2.y, _v2.z, i1);
    const ni2 = dedup.add(vb, vi2, vbstride, _v2.x, _v2.y, _v2.z, i2);
    const ni3 = dedup.add(vb, vi3, vbstride, _v2.x, _v2.y, _v2.z, i3);

    ibNew.push(ni1, ni2, ni3);

    addRemapEntry(i1, ni1);
    addRemapEntry(i2, ni2);
    addRemapEntry(i3, ni3);
  }

  let numNewVerts = dedup.size();
  const nstride = packNormals ? 1 : 3;
  const vbstride2 = vbstride + nstride;
  const vb2 = new Float32Array(numNewVerts * vbstride2);
  const uivb = new Uint16Array(vb2.buffer);
  const dstRest = 3 + nstride;

  dedup.forEach((newIdx, nx, ny, nz, oldIdx) => {

    const dstOff = newIdx * vbstride2;
    const srcOff = oldIdx * vbstride;

    //Copy the position
    for (let i = 0; i < 3; i++) {
      vb2[dstOff + i] = vb[srcOff + i];
    }

    //Insert the normal
    if (packNormals) {
      packNormal(uivb, 2 * (dstOff + 3), nx, ny, nz);
    } else {
      vb2[dstOff + 3] = nx;
      vb2[dstOff + 4] = ny;
      vb2[dstOff + 5] = nz;
    }

    //Copy remaining vertex attributes
    for (let j = 3; j < vbstride; j++) {
      vb2[dstOff + dstRest + j] = vb[srcOff + j];
    }
  });

  //Update the geometry data
  geom.vb = vb2;
  geom.vbstride = vbstride2;

  for (let item in geom.vblayout) {

    //position offset does not change as it comes before the normals
    if (item === "position") {
      continue;
    }

    geom.vblayout[item].offset += nstride;
  }

  //Add the new normal attribute to the vertex layout
  geom.vblayout.normal = {
    bytesPerItem: packNormals ? 2 : 4,
    offset: 3,
    normalized: true,
    itemSize: packNormals ? 2 : 3
  };

  //Remap the index buffer used for edges
  for (let i = 0; i < geom.iblines.length; i++) {
    geom.iblines[i] = vertexRemap[geom.iblines[i]][0];
  }

  //Overwrite index buffer with new vertex indices
  ib.set(ibNew);

}